import { FormikProps } from 'formik';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import addPathPrefix from 'utils/addPathPrefix';
import { FormCardContainer } from 'views/declarations/common/cards/NewFormCard';
import MultipleItemsCard from 'views/declarations/common/MultipleItemsCard';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import DeclarationInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';

export type ArrivalPartyReferences = {
    identificationNumber: string;
    identifier: string;
    type: string;
};

interface CommunicationProps {
    /** The party the communication list hangs off, e.g. `personNotifyingTheArrival`. */
    path: string;
    title: string;
    references: Pick<ArrivalPartyReferences, 'identifier' | 'type'>;
    formik: FormikProps<any>;
    viewOnly?: boolean;
}

/**
 * The one to nine ways of reaching a party. `type` is the communication means - a phone number, an
 * email address - and so comes from CL707 rather than from the party identifier list.
 */
export const ArrivalCommunicationCard = ({ path, title, references, formik, viewOnly }: CommunicationProps) => (
    <MultipleItemsCard
        condensed
        section
        required
        viewOnly={viewOnly}
        title={title}
        path={addPathPrefix(path, 'communication')}
        initialValues={{ identifier: '', type: undefined }}
        list={(entry) => [
            { field: 'Identifier', value: entry.identifier },
            { field: 'Type', value: entry.type },
        ]}
    >
        {(itemPath) => (
            <FormCardContainer oneColumn>
                <DeclarationInput
                    required
                    condensed
                    disabled={viewOnly}
                    maxLength={512}
                    label="Identifier"
                    refNumber={references.identifier}
                    {...getFormikProps(addPathPrefix(itemPath, 'identifier'), formik)}
                />
                <DeclarationSelect
                    hideCodelistMenu
                    required
                    condensed
                    disabled={viewOnly}
                    label="Type"
                    placeholder="Select Type"
                    refNumber={references.type}
                    {...getFormikProps(addPathPrefix(itemPath, 'type'), formik)}
                    codeValidation={[]}
                    codelistProps={{
                        codelistName: ICS2CodelistsMap.CL707,
                        declarationType: DeclarationTypes.ICS2,
                    }}
                />
            </FormCardContainer>
        )}
    </MultipleItemsCard>
);

interface PartyFieldsProps {
    path: string;
    references: ArrivalPartyReferences;
    formik: FormikProps<any>;
    viewOnly?: boolean;
    identificationRequired?: boolean;
}

/**
 * An IE3N06 party: an EORI and its communications. Unlike the ENS parties there is no name and no
 * address, so the shared `PartiesCard` is deliberately not reused here.
 */
export const ArrivalPartyFields = ({
    path,
    references,
    formik,
    viewOnly,
    identificationRequired,
}: PartyFieldsProps) => (
    <>
        <FormCardContainer oneColumn>
            <DeclarationInput
                required={identificationRequired}
                condensed
                disabled={viewOnly}
                maxLength={17}
                label="Identification Number"
                refNumber={references.identificationNumber}
                {...getFormikProps(addPathPrefix(path, 'identificationNumber'), formik)}
            />
        </FormCardContainer>

        <ArrivalCommunicationCard
            path={path}
            title="Communication"
            references={references}
            formik={formik}
            viewOnly={viewOnly}
        />
    </>
);
